import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Activity, Search, Shield, Clock, User } from 'lucide-react';

export const AdminActivityLogsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: logsRes, isLoading } = useQuery({
    queryKey: ['admin-activity-logs'],
    queryFn: () => api.get('/admin/activity-logs').then((res) => res.data.data),
  });

  const logs = logsRes || [];
  const filteredLogs = logs.filter((l: any) => {
    const q = searchTerm.toLowerCase();
    return (
      l.action.toLowerCase().includes(q) ||
      l.entityType.toLowerCase().includes(q) ||
      l.user?.email?.toLowerCase().includes(q) ||
      l.user?.firstName?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-8">
      
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white">
          System Activity &amp; Audit Trail
        </h1>
        <p className="text-xs sm:text-sm text-slate-400">
          Immutable-style security log recording logins, document downloads, project status shifts, and administrative modifications.
        </p>
      </div>

      {/* Search Bar */}
      <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search action, user, or entity..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-brand-500"
          />
        </div>
        <span className="text-xs font-mono text-slate-400">
          Total: {logs.length} Records
        </span>
      </div>

      {/* Logs Table */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-400">Loading audit trail...</div>
      ) : filteredLogs.length === 0 ? (
        <div className="p-12 rounded-2xl bg-slate-950 border border-slate-800 text-center space-y-3">
          <Activity className="w-12 h-12 text-slate-600 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-white">No Audit Records Found</h3>
        </div>
      ) : (
        <div className="bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="bg-slate-900/80 text-slate-400 font-mono text-[11px] uppercase border-b border-slate-800">
                <tr>
                  <th className="px-6 py-4">Timestamp</th>
                  <th className="px-6 py-4">Action</th>
                  <th className="px-6 py-4">Entity</th>
                  <th className="px-6 py-4">User</th>
                  <th className="px-6 py-4">IP / Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 font-mono text-[11px]">
                {filteredLogs.map((log: any) => (
                  <tr key={log.id} className="hover:bg-slate-900/50 transition-colors">
                    <td className="px-6 py-4 text-slate-400 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 font-bold text-brand-300">
                      <span className="px-2 py-0.5 rounded bg-slate-900 border border-slate-800">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-200">
                      {log.entityType} {log.entityId ? `[${log.entityId}]` : ''}
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-sans">
                      {log.user ? `${log.user.firstName} ${log.user.lastName} (${log.user.role})` : 'System / Guest'}
                    </td>
                    <td className="px-6 py-4 text-slate-400 max-w-xs truncate">
                      {log.details ? JSON.stringify(log.details) : log.ipAddress || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};
